/**
 * CollectionGroupManageSheet — 묶음 관리 메뉴 (IA SC-015).
 *
 * 여섯 가지다: 저장 목록(내보내기) · 이름 변경 · 대량 편집기 · 수동으로 추가 · 스캔 카드 ·
 * **컬렉션 삭제**(FN-084, design 2026-08-28 — destructive 라 손이 마지막에 닿는 자리).
 *
 * ⚠ **내보내기는 웹에서만 파일이 된다.** 네이티브 WebView 는 `<a download>` 로 파일을
 *   저장하지 못한다(@capacitor/filesystem 이 의존성에 없다). 조용히 실패하면 사용자는
 *   눌렀는데 아무 일도 안 일어난 것으로 보므로, 앱에서는 안내를 띄운다.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@astryxdesign/core/Button";
import { Dialog, DialogHeader } from "@astryxdesign/core/Dialog";
import { Heading } from "@astryxdesign/core/Heading";
import { TextInput } from "@astryxdesign/core/TextInput";
import { Text } from "@astryxdesign/core/Text";
import { Layout, LayoutContent, LayoutFooter, VStack, HStack } from "@astryxdesign/core/Layout";
import Sheet from "../feedback/Sheet";
import ConfirmDialog from "../feedback/ConfirmDialog";
import Icon, { type IconName } from "../primitives/Icon";
import { useToast } from "../feedback/Toast";
import { useActivityNavigator } from "../../navigation/useActivityNavigator";
import { useOverlayNavigate } from "../../hooks/useOverlayNavigate";
import { isNative } from "../../native";
import { deleteCollectionGroup, renameCollectionGroup, type CollectionGroupCard } from "../../api/collectionGroup";
import { COLLECTION_GROUP_KEY } from "../../hooks/useCollectionGroupQueries";
import { buildGroupCsv, groupCsvFilename } from "../../data/collectionGroupExport";
import {
  COLLECTION_NAME_MAX,
  canSubmitCollectionName,
  clampCollectionName,
  collectionNameLength,
} from "./collectionName";

interface Props {
  open: boolean;
  onClose: () => void;
  groupSerialNo: number;
  groupName: string;
  /** 기본 컬렉션('즐겨찾기')인가 — 이름 변경·삭제 항목이 빠진다 (IA SC-015 · FN-084) */
  isDefault: boolean;
  cards: CollectionGroupCard[];
  bulkEdit: boolean;
  onToggleBulkEdit: () => void;
}

export default function CollectionGroupManageSheet({
  open,
  onClose,
  groupSerialNo,
  groupName,
  isDefault,
  cards,
  bulkEdit,
  onToggleBulkEdit,
}: Props) {
  const { t } = useTranslation("card");
  const toast = useToast();
  const queryClient = useQueryClient();
  const activityNav = useActivityNavigator();
  const overlayNavigate = useOverlayNavigate();

  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [draft, setDraft] = useState(groupName);
  const [busy, setBusy] = useState(false);

  const exportList = () => {
    onClose();
    if (cards.length === 0) {
      toast.info(t("collection.exportEmpty"));
      return;
    }
    if (isNative()) {
      toast.info(t("collection.exportNative"));
      return;
    }
    const now = new Date();
    // BOM — 엑셀이 한글을 깨뜨리지 않게
    const blob = new Blob(["﻿" + buildGroupCsv(groupName, cards, now)], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = groupCsvFilename(groupName, now);
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(t("collection.exportDone", { count: cards.length }));
  };

  const submitRename = async () => {
    const name = draft.trim();
    if (!name || busy) return;
    setBusy(true);
    try {
      await renameCollectionGroup(groupSerialNo, name);
      await queryClient.invalidateQueries({ queryKey: COLLECTION_GROUP_KEY });
      setRenameOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("collection.groupFailed"));
    } finally {
      setBusy(false);
    }
  };

  /* FN-084 — 되돌리기 없음. 담긴 카드는 보유에 그대로 남는다(참조만 사라진다). */
  const submitDelete = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await deleteCollectionGroup(groupSerialNo);
      await queryClient.invalidateQueries({ queryKey: COLLECTION_GROUP_KEY });
      setDeleteOpen(false);
      toast.success(t("collection.deleted", { name: groupName }));
      overlayNavigate("/collections", { replace: true });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("collection.groupFailed"));
    } finally {
      setBusy(false);
    }
  };

  const action = (labelKey: string, descKey: string, icon: IconName, run: () => void) => (
    <Button
      variant="ghost"
      width="100%"
      icon={<Icon name={icon} size="md" />}
      label={t(labelKey)}
      clickAction={run}
      tooltip={t(descKey)}
    />
  );

  return (
    <>
      <Sheet open={open} onClose={onClose} ariaLabel={t("collection.manage")}>
        <VStack gap={2} padding={4}>
          <Heading level={3}>{t("collection.manage")}</Heading>
          {action("collection.action.export", "collection.actionDesc.export", "Download", exportList)}
          {/* 기본 컬렉션은 이름 변경·삭제가 없다 (FN-084). 흐리게 두지 않고 **빼는** 이유는
              그 두 줄이 이 컬렉션에서는 영영 열리지 않아서다 — 비활성 항목은 "지금은 안
              된다" 로 읽혀 조건을 찾게 만든다. 서버도 같은 자리를 거절한다. */}
          {!isDefault
            && action("collection.action.rename", "collection.actionDesc.rename", "Edit", () => {
              setDraft(groupName);
              setRenameOpen(true);
              onClose();
            })}
          {action(
            bulkEdit ? "collection.editDone" : "collection.action.bulkEdit",
            "collection.actionDesc.bulkEdit",
            "List",
            () => { onToggleBulkEdit(); onClose(); },
          )}
          {action("collection.action.addManual", "collection.actionDesc.addManual", "Search", () => {
            onClose();
            activityNav.open({ url: `/collections/groups/${groupSerialNo}/add` });
          })}
          {action("collection.action.addScan", "collection.actionDesc.addScan", "Camera", () => {
            onClose();
            overlayNavigate("/scan");
          })}
          {/* 삭제는 **맨 아래**다 (SC-015 · FN-084) — destructive 는 손이 마지막에 닿는 자리에. */}
          {!isDefault
            && action("collection.action.delete", "collection.actionDesc.delete", "Delete", () => {
              onClose();
              setDeleteOpen(true);
            })}
        </VStack>
      </Sheet>

      <Dialog isOpen={renameOpen} onOpenChange={setRenameOpen} width={360}>
        <Layout
          header={<DialogHeader title={t("collection.renameTitle")} onOpenChange={setRenameOpen} />}
          content={
            <LayoutContent>
              {/* 이름 규칙(FN-035 → FN-033): 20자에서 입력이 멈추고 아래에 카운터가 선다. */}
              {/* ⚠ 카운터는 TextInput **밖**의 Astryx Text 다 (2026-09-03). design 원본은
                  description 자리에 넣고 global.css 로 Astryx Field 의 flex 를 grid 로
                  갈아 라벨 줄 오른쪽에 세웠는데, 그 규칙이 Field 내부 DOM
                  (.astryx-field-label + span)을 :has() 로 파고들어 Astryx 를 올리면
                  조용히 깨진다. 입력 아래 오른쪽 정렬이면 지역 CSS 없이 같은 일을 한다. */}
              <TextInput
                label={t("collection.nameLabel")}
                value={draft}
                onChange={(v) => setDraft(clampCollectionName(v))}
                placeholder={t("collection.namePlaceholder")}
                isDisabled={busy}
              />
              <Text type="supporting" display="block" justify="end">
                {t("collection.nameCount", { n: collectionNameLength(draft), max: COLLECTION_NAME_MAX })}
              </Text>
            </LayoutContent>
          }
          footer={
            <LayoutFooter>
              <HStack gap={2} justify="end">
                <Button
                  variant="ghost"
                  label={t("collection.cancel")}
                  isDisabled={busy}
                  clickAction={() => setRenameOpen(false)}
                />
                <Button
                  variant="primary"
                  label={t("collection.confirmRename")}
                  isDisabled={busy || !canSubmitCollectionName(draft)}
                  clickAction={() => void submitRename()}
                />
              </HStack>
            </LayoutFooter>
          }
        />
      </Dialog>

      {/* FN-084 삭제 확인 — destructive · 되돌리기 없음. 카드가 사라지는 것으로
          오해할 수 있어 "보유에는 그대로 남는다" 를 못박는다. */}
      <ConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        variant="destructive"
        title={t("collection.deleteTitle", { name: groupName })}
        description={t("collection.deleteBody")}
        secondaryText={t("collection.cancel")}
        primaryText={t("collection.deleteConfirm")}
        onSecondaryPressed={() => setDeleteOpen(false)}
        onPrimaryPressed={() => void submitDelete()}
      />
    </>
  );
}
